import React, { FunctionComponent } from 'react';

import { Filters, SearchOperatorOptions } from '@components/show_page/sections/section_search/SearchTypes';
import { TextButton } from '@components/library/button/index';
import filterLogic from '@components/show_page/sections/section_search/filterLogic';

interface SearchDetailsProps {
  filters: Filters;
  filterStrings: Array<string>;
  resetFilters: () => void;
  searchOperator: SearchOperatorOptions;
  searchQuery: string;
  showFullQuery: boolean;
  tagsSearchOperator: "OR" | "AND";
  toggleShowFullQuery: () => void;
}

const SearchDetails: FunctionComponent<SearchDetailsProps> = ({
  filters,
  filterStrings,
  resetFilters,
  searchOperator,
  searchQuery,
  showFullQuery,
  tagsSearchOperator,
  toggleShowFullQuery,
}) => {
  if (filterStrings.length === 0) return <div />; // empty div needed for styling (spacing)

  const stripOperatorsBetweenQuotes = (updatedString: string): string => (
    updatedString.split('"').map((charBlock: string, i: number) => (
      i % 2 === 1 // odd index (blocks of text between quotes are at odd indices)
        ? charBlock.split(` ${searchOperator} `).join(' ') // remove search operator ('AND', 'OR') for character blocks between quotes
        : charBlock
    )).join('"')
  );

  const applySearchOperators = (): string => {
    const initialString = unescape(filterLogic(filters, searchQuery, tagsSearchOperator));
    const trimmedSearchQuery = searchQuery?.trim();
    if (!trimmedSearchQuery) {
      return initialString;
    }

    let updatedString: string;
    if ([SearchOperatorOptions.AND, SearchOperatorOptions.OR].includes(searchOperator)) {
      updatedString = trimmedSearchQuery.split(' ').join(` ${searchOperator} `);
      updatedString = `(${updatedString})`;
    } else {
      // 'exactly this phrase' (Strict) and 'contains this phrase' (Quoted)
      updatedString = (trimmedSearchQuery.startsWith('"') && trimmedSearchQuery.endsWith('"'))
        ? trimmedSearchQuery // don't add double quotes if it's already double quoted
        : `"${trimmedSearchQuery}"`;
    }

    return initialString.replace(`(${trimmedSearchQuery})`, stripOperatorsBetweenQuotes(updatedString));
  };

  return (
    <div className="search-details-container">
      <div className="filters-results-row results-row">
        <div className="filters-results-wrap">
          <div className="filters-results-list show-results">
            <span>{t('Active Filters')}: </span>
            {filterStrings.map((item, i) => (
              <span key={`item-${i}`}>
                {item}
                {i < filterStrings.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
          <div className="clickable-options">
            <TextButton
              className="exact-query-group"
              icon="bff-eye"
              iconSize={16}
              onClick={toggleShowFullQuery}
            >
              {showFullQuery ? t("Hide Query") : t("View Query")}
            </TextButton>
            <TextButton
              className="reset-filters"
              icon="bff-refresh"
              onClick={resetFilters}
            >
              {t("Reset Filters")}
            </TextButton>
          </div>
        </div>
      </div>
      {showFullQuery && (
        <div className="filters-results-row query-row">
          <div className="filters-results-wrap">
            <div className="filters-results-list exact-query">
              {`${t('Query')}: `}
              {applySearchOperators()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchDetails;
